import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, apiError, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { ArrowLeft, Save, Archive, Trash2, Upload, X, Plus, Car, User, CalendarDays, FileText, Play, Pause, Square, RotateCcw, Timer, Maximize2, AlertTriangle, History, Phone, Mail, Image as ImageIcon, ChevronDown, ChevronRight, Building2, ClipboardList, FolderOpen, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ColorChip } from "@/components/ColorChip";
import { DirectPaymentDialog } from "@/pages/Payments";
import { Wallet } from "lucide-react";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { fmtEurCompact as fmtEur } from "@/lib/formatMoney";

const STATUS_OPTIONS = [
  { v: "angelegt", label: "Angelegt" },
  { v: "in_bearbeitung", label: "In Bearbeitung" },
  { v: "fertig", label: "Fertig" },
  { v: "archiviert", label: "Archiviert" },
];

const STATUS_STYLES = {
  angelegt: {
    trigger: "border-amber-200 bg-amber-50 text-amber-700",
    dot: "bg-amber-400",
  },
  in_bearbeitung: {
    trigger: "border-blue-200 bg-blue-50 text-blue-700",
    dot: "bg-blue-500",
  },
  fertig: {
    trigger: "border-emerald-200 bg-emerald-50 text-emerald-700",
    dot: "bg-emerald-500",
  },
  archiviert: {
    trigger: "border-slate-200 bg-slate-100 text-slate-600",
    dot: "bg-slate-400",
  },
};

const fmtDT = (iso) => iso ? new Date(iso).toLocaleString("de-DE") : "—";

const splitCustomerAddress = (value = "") => {
  const normalized = value.replace(/,\s*(?=\d{5}\s)/, "\n");
  const parts = normalized.split("\n").map((part) => part.trim()).filter(Boolean);
  const location = parts.length > 1 ? parts[parts.length - 1] : "";
  const match = location.match(/^(\d{5})\s+(.+)$/);
  return {
    street: match ? parts.slice(0, -1).join(", ") : value,
    zip: match?.[1] || "",
    city: match?.[2] || "",
  };
};

function JobSectionHeading({ number, icon: Icon, title }) {
  return (
    <div className="stellar-job-section-heading">
      <span className="stellar-job-section-step">{number}</span>
      <Icon className="h-4 w-4" />
      <h2 className="font-display text-base font-bold">{title}</h2>
    </div>
  );
}

function PhotoUploader({ jobId, photos, onChanged, onPreview, readOnly = false }) {
  const upload = async (e, category) => {
    const files = e.target.files;
    if (!files?.length) return;
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      fd.append("category", category);
      try {
        await api.post(`/jobs/${jobId}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" }});
      } catch (err) { toast.error(apiError(err)); }
    }
    onChanged?.();
    e.target.value = "";
  };
  const remove = async (pid) => {
    try { await api.delete(`/jobs/${jobId}/photos/${pid}`); onChanged?.(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const cats = [
    { key: "before", label: "Vorher" },
    { key: "after", label: "Nachher" },
    { key: "documents", label: "Dokumente" },
  ];
  return (
    <div className="space-y-3">
      {cats.map((c) => {
        const list = (photos || []).filter((p) => p.category === c.key);
        return (
          <div key={c.key}>
            <div className="flex items-center justify-between mb-2">
              <div className="text-xs uppercase tracking-widest text-muted-foreground">{c.label}</div>
              {!readOnly && (
                <label className="inline-flex items-center gap-1 text-xs text-primary cursor-pointer hover:underline" data-testid={`job-upload-${c.key}`}>
                  <Upload className="h-3 w-3" /> Hochladen
                  <input type="file" multiple className="hidden" accept="image/*,application/pdf" onChange={(e) => upload(e, c.key)} />
                </label>
              )}
            </div>
            {list.length === 0 ? (
              <div className="text-xs text-muted-foreground italic">Keine Dateien</div>
            ) : (
              <div className="grid grid-cols-3 gap-2">
                {list.map((p) => (
                  <div key={p.id} className="relative group">
                    {p.content_type?.startsWith("image/") ? (
                      <button
                        type="button"
                        className="block w-full cursor-zoom-in"
                        onClick={() => onPreview?.(p.id)}
                        aria-label={`${p.filename || "Foto"} vergrößern`}
                      >
                        <img src={fileUrl(p.storage_path)} alt={p.filename} className="w-full h-24 object-cover rounded-md border transition-transform hover:scale-[1.02]" />
                      </button>
                    ) : (
                      <a href={fileUrl(p.storage_path)} target="_blank" rel="noreferrer" className="block h-24 rounded-md border flex items-center justify-center text-xs bg-muted">{p.filename}</a>
                    )}
                    {!readOnly && (
                      <button onClick={() => remove(p.id)} className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100" type="button">
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function JobServicePicker({ services = [], onPick, onCustom }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700" data-testid="job-add-service">
          <Plus className="h-4 w-4 mr-1" /> Leistung hinzufügen
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[320px] p-0" align="end">
        <Command>
          <CommandInput placeholder="Katalog durchsuchen…" />
          <CommandList>
            <CommandEmpty>Keine Treffer</CommandEmpty>
            <CommandGroup heading="Aus Katalog">
              {services.map((s) => (
                <CommandItem
                  key={s.id}
                  value={s.name}
                  onSelect={() => { onPick(s); setOpen(false); }}
                  data-testid={`job-picker-service-${s.id}`}
                >
                  <span className="h-2.5 w-2.5 rounded-full mr-2" style={{ background: s.color }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm">{s.name}</div>
                    <div className="text-xs text-muted-foreground">
                      {s.default_duration_min} Min{Number(s.default_price) > 0 ? ` · ${fmtEur(s.default_price)}` : ""}
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
            <CommandGroup>
              <CommandItem
                value="custom-free-text-job"
                onSelect={() => { onCustom?.(); setOpen(false); }}
                data-testid="job-picker-custom"
              >
                <Plus className="h-3.5 w-3.5 mr-2" /> Eigene Leistung (Freitext)
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState(null);

  const { data: job } = useQuery({
    queryKey: ["job", id],
    queryFn: async () => (await api.get(`/jobs/${id}`)).data,
  });

  const { data: catalog = [] } = useQuery({
    queryKey: ["services"],
    queryFn: async () => (await api.get("/services")).data,
  });

  const { data: customer } = useQuery({
    queryKey: ["customer", job?.customer_id],
    queryFn: async () => (await api.get(`/customers/${job.customer_id}`)).data,
    enabled: !!job?.customer_id,
  });

  const { data: jobPayments = [] } = useQuery({
    queryKey: ["payments", "job", id],
    queryFn: async () => (await api.get("/payments", { params: { job_id: id } })).data,
    enabled: !!job,
  });

  const { data: jobInvoices = [] } = useQuery({
    queryKey: ["invoices", "job", id],
    queryFn: async () => (await api.get("/invoices", { params: { job_id: id } })).data,
    enabled: !!job,
  });

  const [saveBillingToCustomer, setSaveBillingToCustomer] = useState(true);
  const [companyBilling, setCompanyBilling] = useState(false);

  useEffect(() => {
    if (job) {
      const storedBilling = job.billing_details || {};
      const storedAddress = splitCustomerAddress(customer?.address || "");
      const hasCompanyData = !!(
        storedBilling.company_name
        || storedBilling.contact_person
        || storedBilling.customer_reference
      );
      setForm({
        title: job.title || "",
        status: job.status,
        notes: job.notes || "",
        total_price: job.total_price ?? 0,
        services: job.services || [],
        billing_details: {
          enabled: storedBilling.enabled ?? true,
          recipient: storedBilling.recipient || customer?.name || job.customer_name || "",
          company_name: storedBilling.company_name || "",
          contact_person: storedBilling.contact_person || "",
          street: storedBilling.street || storedAddress.street,
          zip: storedBilling.zip || storedAddress.zip,
          city: storedBilling.city || storedAddress.city,
          invoice_email: storedBilling.invoice_email || customer?.email || "",
          customer_reference: storedBilling.customer_reference || "",
        },
      });
      setCompanyBilling(hasCompanyData);
      setSaveBillingToCustomer(!hasCompanyData);
    }
  }, [job, customer]);

  // Sprint 16 — Fetch the source appointment for the Auftragsübersicht card
  const { data: sourceAppt } = useQuery({
    queryKey: ["appointment", job?.appointment_id],
    queryFn: async () => job?.appointment_id ? (await api.get(`/appointments/${job.appointment_id}`)).data : null,
    enabled: !!job?.appointment_id,
  });

  const [showBilling, setShowBilling] = useState(false);
  useEffect(() => {
    if (job?.billing_details?.enabled) setShowBilling(true);
  }, [job?.billing_details?.enabled]);

  const [photoDialogOpen, setPhotoDialogOpen] = useState(false);
  const [lightboxId, setLightboxId] = useState(null);

  const saveMut = useMutation({
    mutationFn: async () => {
      const billingDetails = {
        ...form.billing_details,
        enabled: true,
        company_name: companyBilling ? form.billing_details.company_name : "",
        contact_person: companyBilling ? form.billing_details.contact_person : "",
        customer_reference: companyBilling ? form.billing_details.customer_reference : "",
      };
      const savedJob = (await api.put(`/jobs/${id}`, {
        customer_id: job.customer_id,
        vehicle_id: job.vehicle_id,
        appointment_id: job.appointment_id,
        title: form.title,
        services: form.services,
        status: form.status,
        total_price: Number(form.total_price) || 0,
        notes: form.notes,
        start_time: job.start_time,
        end_time: job.end_time,
        billing_details: billingDetails,
        save_billing_to_customer: saveBillingToCustomer,
      })).data;
      return savedJob;
    },
    onSuccess: (savedJob) => {
      toast.success(savedJob.customer_details_updated ? "Auftrag und Kundendaten gespeichert" : "Gespeichert");
      qc.invalidateQueries({ queryKey: ["job", id] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
      qc.invalidateQueries({ queryKey: ["customer", job.customer_id] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const billingSaveMut = useMutation({
    mutationFn: async () => {
      const billingDetails = {
        ...form.billing_details,
        enabled: true,
        company_name: companyBilling ? form.billing_details.company_name : "",
        contact_person: companyBilling ? form.billing_details.contact_person : "",
        customer_reference: companyBilling ? form.billing_details.customer_reference : "",
      };
      return (await api.put(`/jobs/${id}/billing-details`, {
        billing_details: billingDetails,
        save_billing_to_customer: saveBillingToCustomer,
      })).data;
    },
    onSuccess: (savedJob) => {
      toast.success(savedJob.customer_details_updated ? "Rechnungsdaten und Kundendaten gespeichert" : "Rechnungsdaten gespeichert");
      qc.invalidateQueries({ queryKey: ["job", id] });
      qc.invalidateQueries({ queryKey: ["customer", job.customer_id] });
    },
    onError: (e) => toast.error(apiError(e)),
  });

  const statusMut = useMutation({
    mutationFn: async (status) => (await api.patch(`/jobs/${id}/status`, { status })).data,
    onSuccess: () => { toast.success("Status aktualisiert"); qc.invalidateQueries({ queryKey: ["job", id] }); qc.invalidateQueries({ queryKey: ["jobs"] }); },
    onError: (e) => toast.error(apiError(e)),
  });

  const archiveMut = useMutation({
    mutationFn: async () => (await api.delete(`/jobs/${id}`)).data,
    onSuccess: () => { toast.success("Auftrag archiviert"); qc.invalidateQueries({ queryKey: ["jobs"] }); navigate("/auftraege"); },
  });

  const hardDeleteMut = useMutation({
    mutationFn: async () => (await api.delete(`/jobs/${id}?hard=true`)).data,
    onSuccess: () => { toast.success("Auftrag gelöscht"); qc.invalidateQueries({ queryKey: ["jobs"] }); navigate("/auftraege"); },
    onError: (e) => toast.error(apiError(e)),
  });

  // Sprint 24 — Arbeit abschließen / Auftrag wieder öffnen (single-source status transitions).
  const completeMut = useMutation({
    mutationFn: async () => (await api.post(`/jobs/${id}/complete`)).data,
    onSuccess: () => { toast.success("Auftrag abgeschlossen"); qc.invalidateQueries({ queryKey: ["job", id] }); qc.invalidateQueries({ queryKey: ["jobs"] }); qc.invalidateQueries({ queryKey: ["active-timer-job"] }); qc.invalidateQueries({ queryKey: ["timer-candidates"] }); },
    onError: (e) => toast.error(apiError(e)),
  });
  const reopenMut = useMutation({
    mutationFn: async () => (await api.post(`/jobs/${id}/reopen`)).data,
    onSuccess: () => { toast.success("Auftrag wieder geöffnet"); qc.invalidateQueries({ queryKey: ["job", id] }); qc.invalidateQueries({ queryKey: ["jobs"] }); },
    onError: (e) => toast.error(apiError(e)),
  });

  const invoiceMut = useMutation({
    mutationFn: async () => (await api.post(`/jobs/${id}/create-invoice`)).data,
    onSuccess: (inv) => {
      toast.success("Rechnungsentwurf erstellt");
      qc.invalidateQueries({ queryKey: ["invoices", "job", id] });
      qc.invalidateQueries({ queryKey: ["payments", "job", id] });
      navigate(`/rechnungen/${inv.id}`);
    },
    onError: (e) => toast.error(apiError(e)),
  });

  // Sprint 23 — Direktzahlung dialog state (reuses the existing DirectPaymentDialog).
  const [directPayOpen, setDirectPayOpen] = useState(false);
  const [settleOpen, setSettleOpen] = useState(false);
  const supplementInvoiceMut = useMutation({
    mutationFn: async () => (await api.post(`/jobs/${id}/create-supplement-invoice`)).data,
    onSuccess: (inv) => {
      toast.success("Zusatzrechnung als Entwurf erstellt");
      qc.invalidateQueries({ queryKey: ["invoices", "job", id] });
      qc.invalidateQueries({ queryKey: ["payments", "job", id] });
      setSettleOpen(false);
      navigate(`/rechnungen/${inv.id}`);
    },
    onError: (e) => toast.error(apiError(e)),
  });

  // Sprint 17 — Repair broken legacy jobs (missing customer/vehicle/services) from linked appointment
  const repairMut = useMutation({
    mutationFn: async () => (await api.post(`/jobs/${id}/repair-from-appointment`)).data,
    onSuccess: () => { toast.success("Auftrag aus Termin repariert"); qc.invalidateQueries({ queryKey: ["job", id] }); },
    onError: (e) => toast.error(apiError(e)),
  });
  const convertBusy = false;

  if (!job || !form) return <div className="p-8 text-muted-foreground">Laden…</div>;

  const isLocked = ["fertig", "archiviert"].includes(job.status);
  const paidTotal = jobPayments
    .filter((payment) => payment.status !== "storniert")
    .reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0);
  const openTotal = Math.max(0, (Number(job.total_price) || 0) - paidTotal);
  const activeInvoices = jobInvoices.filter((invoice) => invoice.status !== "storniert");
  const activeInvoice = activeInvoices[0];
  const activeInvoiceIds = new Set(activeInvoices.map((invoice) => invoice.id));
  const activeInvoiceTotal = activeInvoices.reduce((sum, invoice) => sum + (Number(invoice.total) || 0), 0);
  const linkedInvoicePaid = jobPayments
    .filter((payment) => payment.status !== "storniert" && activeInvoiceIds.has(payment.invoice_id))
    .reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0);
  const directPaid = jobPayments
    .filter((payment) => payment.status !== "storniert" && payment.type === "direct" && !payment.invoice_id)
    .reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0);
  const invoiceOpenTotal = Math.max(0, activeInvoiceTotal - linkedInvoicePaid);
  const unbilledTotal = Math.max(0, (Number(job.total_price) || 0) - activeInvoiceTotal);
  const unbilledOpenTotal = Math.max(0, unbilledTotal - directPaid);
  const isPaid = (Number(job.total_price) || 0) > 0 && openTotal < 0.01;
  const latestPayment = jobPayments[0];

  const addService = () => setForm({ ...form, services: [...form.services, { service_name: "", price: 0, duration_min: 0 }] });
  const addServiceFromCatalog = (s) => setForm({
    ...form,
    services: [
      ...form.services,
      { service_id: s.id, service_name: s.name, price: Number(s.default_price) || 0, duration_min: s.default_duration_min || 0 },
    ],
  });
  const updateService = (idx, key, value) => {
    const s = [...form.services];
    s[idx] = { ...s[idx], [key]: value };
    setForm({ ...form, services: s });
  };
  const removeService = (idx) => {
    setForm({ ...form, services: form.services.filter((_, i) => i !== idx) });
  };
  const recalcTotal = () => {
    const sum = form.services.reduce((a, s) => a + (Number(s.price) || 0), 0);
    setForm({ ...form, total_price: sum });
  };

  const photos = job.photos || [];
  const beforeCount = photos.filter((p) => p.category === "before").length;
  const afterCount  = photos.filter((p) => p.category === "after").length;
  const docsCount   = photos.filter((p) => p.category === "documents").length;
  const setBD = (k, v) => setForm({ ...form, billing_details: { ...form.billing_details, [k]: v } });
  const statusStyle = STATUS_STYLES[form.status] || STATUS_STYLES.angelegt;

  return (
    <div className="stellar-job-page">
      <Link to="/auftraege" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="back-to-jobs">
        <ArrowLeft className="h-4 w-4" /> Aufträge
      </Link>

      <div className="stellar-job-heading mb-6 flex flex-wrap items-center gap-4">
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight break-words">{job.title || "Auftrag"}</h1>
        {isLocked ? (
          <div className={`inline-flex min-w-32 items-center justify-center rounded-full border px-4 py-2 text-sm font-medium ${statusStyle.trigger}`} data-testid="job-status-readonly">
            <span className={`h-2.5 w-2.5 rounded-full mr-2 ${statusStyle.dot}`} />
            {STATUS_OPTIONS.find((s) => s.v === form.status)?.label || form.status}
          </div>
        ) : (
          <Select value={form.status} onValueChange={(v) => { setForm({ ...form, status: v }); statusMut.mutate(v); }}>
            <SelectTrigger className={`w-auto min-w-36 rounded-full ${statusStyle.trigger}`} data-testid="job-status-select">
              <span className={`h-2.5 w-2.5 rounded-full mr-1 ${statusStyle.dot}`} />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>{STATUS_OPTIONS.map(s => <SelectItem key={s.v} value={s.v}>{s.label}</SelectItem>)}</SelectContent>
          </Select>
        )}
        {job.completed_at && (
          <span className="text-xs text-muted-foreground">Abgeschlossen am {fmtDT(job.completed_at)}</span>
        )}
      </div>

      {isLocked && (
        <section className="stellar-completion-summary mb-4" data-testid="job-completion-summary">
          <div className="stellar-completion-title">
            <span className="stellar-completion-check"><CheckCircle2 /></span>
            <div>
              <div className="font-display text-lg font-semibold">Auftrag abgeschlossen</div>
              <div className="text-xs text-slate-500">Finale Arbeits- und Zahlungsübersicht</div>
            </div>
          </div>
          <div className="stellar-completion-stats">
            <div>
              <span>Auftragswert</span>
              <strong>{fmtEur(job.total_price)}</strong>
            </div>
            <div className="is-paid">
              <span>Bezahlt</span>
              <strong>{fmtEur(paidTotal)}</strong>
            </div>
            <div className={openTotal > 0 ? "is-open" : "is-paid"}>
              <span>Offen</span>
              <strong>{fmtEur(openTotal)}</strong>
            </div>
            <div>
              <span>Arbeitszeit</span>
              <strong>{fmtHumanTotal(job.total_work_seconds || 0)}</strong>
            </div>
          </div>
          <div className="stellar-completion-meta">
            <span>Abgeschlossen: <strong>{fmtDT(job.completed_at)}</strong></span>
            {latestPayment ? (
              <span>Letzte Zahlung: <strong>{latestPayment.payment_date} · {latestPayment.payment_method}</strong></span>
            ) : (
              <span>Noch keine Zahlung erfasst</span>
            )}
          </div>
        </section>
      )}

      {/* ─── 1. Auftragsübersicht ─── */}
      <section className="stellar-job-section">
      <JobSectionHeading number="1" icon={User} title="Kunde, Fahrzeug & Termin" />
      <div className="premium-card stellar-overview" data-testid="job-overview">
        <div className="flex items-center">
          {!isLocked && (!job.customer_id || (!job.vehicle_id && sourceAppt?.car_model) || (form.services.length === 0 && sourceAppt?.services?.length > 0)) && job.appointment_id && (
            <Button
              size="sm"
              variant="outline"
              className="ml-auto"
              onClick={() => repairMut.mutate()}
              disabled={repairMut.isPending}
              data-testid="repair-from-appointment"
              title="Fehlende Daten aus dem verknüpften Termin übernehmen"
            >
              <RotateCcw className="h-3.5 w-3.5 mr-1" /> {repairMut.isPending ? "Reparatur…" : "Aus Termin reparieren"}
            </Button>
          )}
        </div>
        <div className="stellar-overview-grid">
          <div className="stellar-overview-item">
            <div className="stellar-icon-bubble stellar-icon-green"><User /></div>
            <div className="min-w-0">
            <div className="stellar-eyebrow">Kunde</div>
            <div className="stellar-overview-title">
              {job.customer_id ? (
                <Link to={`/kunden/${job.customer_id}`} className="hover:underline" data-testid="overview-customer">
                  {job.customer_name || "—"}
                </Link>
              ) : <span className="text-destructive">Nicht verknüpft</span>}
            </div>
            {(sourceAppt?.phone || sourceAppt?.email) && (
              <div className="stellar-meta-list">
                {sourceAppt.phone && (
                  <a
                    href={`tel:${String(sourceAppt.phone).replace(/[^\d+]/g, "")}`}
                    className="hover:text-emerald-700 hover:underline"
                    title={`${sourceAppt.phone} anrufen`}
                    data-testid="call-job-customer"
                  >
                    <Phone /> {sourceAppt.phone}
                  </a>
                )}
                {sourceAppt.email && <span><Mail /> {sourceAppt.email}</span>}
              </div>
            )}
            </div>
            <ChevronRight className="stellar-mobile-chevron" />
          </div>
          <div className="stellar-overview-item">
            <div className="stellar-icon-bubble stellar-icon-violet"><Car /></div>
            <div className="min-w-0">
            <div className="stellar-eyebrow">Fahrzeug</div>
            <div className="stellar-overview-title">
              {job.vehicle_id ? (
                <Link to={`/fahrzeuge/${job.vehicle_id}`} className="hover:underline" data-testid="overview-vehicle">
                  {job.vehicle_display || "—"}
                </Link>
              ) : (sourceAppt?.car_model || "—")}
            </div>
            {job.vehicle_id && job.vehicle_color && (
              <div className="stellar-vehicle-meta"><ColorChip value={job.vehicle_color} testId="job-vehicle-color" /></div>
            )}
            {!job.vehicle_id && sourceAppt && (sourceAppt.vehicle_year || sourceAppt.plate || sourceAppt.vehicle_color) && (
              <div className="stellar-vehicle-meta">
                {sourceAppt.vehicle_year && <span>Bj. {sourceAppt.vehicle_year}</span>}
                {sourceAppt.plate && <span>{sourceAppt.plate}</span>}
                {sourceAppt.vehicle_color && (
                  <>
                    <span className="text-muted-foreground/60">·</span>
                    <ColorChip value={sourceAppt.vehicle_color} testId="job-source-vehicle-color" />
                  </>
                )}
              </div>
            )}
            </div>
            <ChevronRight className="stellar-mobile-chevron" />
          </div>
          {sourceAppt && (
            <div className="stellar-overview-item">
              <div className="stellar-icon-bubble stellar-icon-blue"><CalendarDays /></div>
              <div className="min-w-0">
              <div className="stellar-eyebrow">Termin</div>
              <div className="stellar-overview-title">
                {new Date(sourceAppt.start).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" })}
                {" – "}
                {new Date(sourceAppt.end).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" })}
              </div>
              <Link to={`/kalender?open=${job.appointment_id}`} className="stellar-source-link" data-testid="job-appointment-link">
                <CalendarDays className="h-3 w-3" /> Ursprünglicher Termin
              </Link>
              {(sourceAppt?.estimated_price > 0) && (
                <div className="text-xs text-muted-foreground mt-2">Geschätzt: <strong className="text-foreground">{fmtEur(sourceAppt.estimated_price)}</strong></div>
              )}
              </div>
              <ChevronRight className="stellar-mobile-chevron" />
            </div>
          )}
        </div>
      </div>
      </section>

      {/* ─── 2. Leistungen ─── */}
      <section className="stellar-job-section">
      <JobSectionHeading number="2" icon={ClipboardList} title="Leistungen & Gesamtbetrag" />
      <div className="premium-card overflow-hidden">
        <div className="p-4 border-b border-border/60 flex items-center gap-2">
          <div className="stellar-section-icon stellar-section-green"><ClipboardList /></div>
          <div className="font-display font-semibold">Leistungen ({form.services.length})</div>
          {!isLocked && (
            <div className="ml-auto">
              <JobServicePicker services={catalog} onPick={addServiceFromCatalog} onCustom={addService} />
            </div>
          )}
        </div>
        <div className="divide-y divide-border/60">
          {form.services.length === 0 && <div className="p-4 text-sm text-muted-foreground">Keine Leistungen hinzugefügt.</div>}
          {form.services.map((s, idx) => isLocked ? (
            <div key={idx} className="stellar-completed-service p-4 flex items-center gap-3 min-w-0" data-testid={`job-service-${idx}`}>
              <span className="stellar-service-check"><CheckCircle2 /></span>
              <div className="flex-1 min-w-0 font-medium">{s.service_name || "Leistung"}</div>
              <div className="font-display font-semibold text-slate-900">{fmtEur(s.price)}</div>
            </div>
          ) : (
            <div key={idx} className="p-3 flex items-center gap-2 min-w-0" data-testid={`job-service-${idx}`}>
              <Input value={s.service_name} onChange={(e) => updateService(idx, "service_name", e.target.value)} placeholder="Leistungsbezeichnung" className="flex-1 min-w-0" />
              <Input type="number" step="0.01" value={s.price} onChange={(e) => updateService(idx, "price", Number(e.target.value) || 0)} className="w-20 sm:w-24 shrink-0" placeholder="Preis" />
              <Button size="icon" variant="ghost" onClick={() => removeService(idx)} className="shrink-0"><X className="h-4 w-4 text-destructive" /></Button>
            </div>
          ))}
        </div>
        <div className="p-3 border-t border-border/60 flex items-center gap-2">
          <div className="text-sm font-semibold">Gesamt:</div>
          {isLocked ? (
            <div className="ml-auto font-display text-lg font-bold text-emerald-700" data-testid="job-total-price">{fmtEur(job.total_price)}</div>
          ) : (
            <>
              <Input type="number" step="0.01" value={form.total_price} onChange={(e) => setForm({ ...form, total_price: e.target.value })} className="stellar-total-price w-32 ml-auto" data-testid="job-total-price" />
              <Button size="sm" variant="outline" onClick={recalcTotal} title="Aus Leistungen berechnen">Summieren</Button>
            </>
          )}
        </div>
      </div>
      </section>

      {/* ─── 3. Rechnungsdaten (collapsible) ─── */}
      <section className="stellar-job-section">
      <JobSectionHeading number="3" icon={FileText} title="Auftragsdetails" />
      <div className="stellar-job-details-grid">
      <div className="premium-card overflow-hidden">
        <button
          type="button"
          onClick={() => setShowBilling((s) => !s)}
          data-testid="billing-toggle"
          title="Diese Daten werden beim Erstellen der Rechnung automatisch übernommen."
          className="w-full flex items-center gap-2 p-4 hover:bg-muted/40 text-left"
        >
          {showBilling ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
          <Building2 className="h-4 w-4 text-muted-foreground" />
          <div className="font-display font-semibold flex-1">Rechnungsdaten</div>
          <span className={`text-[10px] px-2 py-0.5 rounded-full ${
            form.billing_details?.street && form.billing_details?.zip && form.billing_details?.city
              ? "bg-emerald-100 text-emerald-700"
              : "bg-amber-100 text-amber-700"
          }`}>
            {form.billing_details?.street && form.billing_details?.zip && form.billing_details?.city ? "Vollständig" : "Ergänzen"}
          </span>
        </button>
        {showBilling && (
          <fieldset disabled={job.status === "archiviert"} className="p-4 border-t border-border/60 space-y-3 disabled:opacity-75" data-testid="billing-section">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div><Label>Rechnungsempfänger</Label><Input value={form.billing_details.recipient || ""} onChange={(e) => setBD("recipient", e.target.value)} placeholder="Vor- und Nachname" data-testid="billing-recipient" /></div>
              <div><Label>Rechnungs-E-Mail</Label><Input type="email" value={form.billing_details.invoice_email || ""} onChange={(e) => setBD("invoice_email", e.target.value)} data-testid="billing-email" /></div>
              <div className="sm:col-span-2"><Label>Straße und Hausnummer</Label><Input value={form.billing_details.street || ""} onChange={(e) => setBD("street", e.target.value)} data-testid="billing-street" /></div>
              <div><Label>PLZ</Label><Input value={form.billing_details.zip || ""} onChange={(e) => setBD("zip", e.target.value)} data-testid="billing-zip" /></div>
              <div><Label>Ort</Label><Input value={form.billing_details.city || ""} onChange={(e) => setBD("city", e.target.value)} data-testid="billing-city" /></div>
            </div>
            <label className="flex items-center gap-2 rounded-lg border border-border/60 bg-muted/30 p-3 text-sm">
              <input
                type="checkbox"
                checked={companyBilling}
                onChange={(e) => {
                  setCompanyBilling(e.target.checked);
                  if (e.target.checked) setSaveBillingToCustomer(false);
                }}
                data-testid="billing-company-enabled"
              />
              Firma / abweichende Rechnungsdaten
            </label>
            {companyBilling && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 rounded-lg border border-violet-200 bg-violet-50/40 p-3">
                <div><Label>Firmenname</Label><Input value={form.billing_details.company_name || ""} onChange={(e) => setBD("company_name", e.target.value)} data-testid="billing-company" /></div>
                <div><Label>Ansprechpartner</Label><Input value={form.billing_details.contact_person || ""} onChange={(e) => setBD("contact_person", e.target.value)} /></div>
                <div className="sm:col-span-2"><Label>Kundenreferenz / Bestell-Nr.</Label><Input value={form.billing_details.customer_reference || ""} onChange={(e) => setBD("customer_reference", e.target.value)} data-testid="billing-reference" /></div>
              </div>
            )}
            <label className="flex items-start gap-2 text-sm" title="Name, Rechnungs-E-Mail und Adresse in der Kundenakte aktualisieren.">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={saveBillingToCustomer}
                onChange={(e) => setSaveBillingToCustomer(e.target.checked)}
                data-testid="billing-save-customer"
              />
              <span>Kontaktdaten beim Kunden speichern</span>
            </label>
            {job.status === "fertig" && (
              <div className="flex justify-end pt-2">
                <Button
                  type="button"
                  onClick={() => billingSaveMut.mutate()}
                  disabled={billingSaveMut.isPending}
                  data-testid="save-billing-details"
                >
                  <Save className="h-4 w-4 mr-1" />
                  Rechnungsdaten speichern
                </Button>
              </div>
            )}
          </fieldset>
        )}
      </div>

        <div className="premium-card p-5 space-y-3 stellar-job-notes-card">
          {isLocked ? (
            <>
              <div title="Nur intern sichtbar. Kundenseitige Angaben gehören in die Rechnungsnotiz.">
                <div className="stellar-eyebrow">Titel</div>
                <div className="mt-1 font-display text-lg font-semibold" data-testid="job-title">{job.title || "Auftrag"}</div>
              </div>
              <div className="border-t border-slate-100 pt-3">
                <div className="stellar-eyebrow">Interne Notizen</div>
                <div className="mt-2 whitespace-pre-wrap text-sm text-slate-600" data-testid="job-notes">
                  {job.notes || "Keine internen Notizen."}
                </div>
              </div>
            </>
          ) : (
            <>
              <div>
                <Label>Titel</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="job-title" />
              </div>
              <div>
                <Label>Interne Notizen</Label>
                <Textarea rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="job-notes" placeholder="Nur intern sichtbar. Erscheint NICHT auf Rechnungen." />
              </div>
            </>
          )}
        </div>
      </div>
      </section>

      {/* ─── 4. Werkstatt + Dokumente ─── */}
      <section className="stellar-job-section">
      <JobSectionHeading number="4" icon={Timer} title="Werkstatt & Dokumente" />
      <div className="stellar-work-grid">
        <JobTimerCompact job={job} />
        <button
          type="button"
          onClick={() => setPhotoDialogOpen(true)}
          data-testid="open-photos-dialog"
          title="Fotos und Dokumente des Auftrags öffnen."
          className="premium-card stellar-photo-card p-5 text-left hover:bg-slate-50 transition"
        >
          <div className="flex items-center gap-2">
            <div className="stellar-section-icon stellar-section-violet"><ImageIcon /></div>
            <div className="font-display font-semibold flex-1">Fotos & Dokumente</div>
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          </div>
          <div className="mt-2 text-xs text-muted-foreground space-x-3">
            <span>Vorher: <span className="font-medium text-foreground" data-testid="count-before">{beforeCount}</span></span>
            <span>Nachher: <span className="font-medium text-foreground" data-testid="count-after">{afterCount}</span></span>
            <span>Dokumente: <span className="font-medium text-foreground" data-testid="count-docs">{docsCount}</span></span>
          </div>
          <div className="hidden lg:inline-flex mt-6 items-center gap-2 rounded-xl border border-slate-200 bg-white px-4 py-2 text-sm font-medium">
            <FolderOpen className="h-4 w-4" /> Öffnen
          </div>
        </button>
      </div>
      </section>

      {/* ─── 5. Actions ─── */}
      <section className="stellar-job-section">
      <JobSectionHeading number="5" icon={CheckCircle2} title="Aktionen" />
      <div className="premium-card stellar-job-actions flex flex-wrap gap-3 p-4">
        {!isLocked && (
          <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => saveMut.mutate()} disabled={saveMut.isPending} data-testid="save-job"><Save className="h-4 w-4 mr-1" /> Speichern</Button>
        )}
        {activeInvoice ? (
          activeInvoices.map((invoice, index) => (
            <Button key={invoice.id} asChild className="border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100" variant="outline" data-testid={index === 0 ? "open-invoice-from-job" : `open-invoice-${index}-from-job`}>
              <Link to={`/rechnungen/${invoice.id}`}>
                <FileText className="h-4 w-4 mr-1" /> {activeInvoices.length > 1 ? (invoice.invoice_number || `Entwurf ${index + 1}`) : "Rechnung öffnen"}
              </Link>
            </Button>
          ))
        ) : (
          <Button className="border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100" variant="outline" onClick={() => invoiceMut.mutate()} disabled={invoiceMut.isPending || convertBusy} data-testid="create-invoice-from-job">
            <FileText className="h-4 w-4 mr-1" /> {paidTotal > 0 ? "Rechnung nachträglich erstellen" : "Rechnung erstellen"}
          </Button>
        )}
        {!activeInvoice && !isPaid && openTotal > 0 && (
          <Button className="border-amber-200 bg-amber-50 text-amber-700 hover:bg-amber-100" variant="outline" onClick={() => setDirectPayOpen(true)} data-testid="direct-payment-from-job">
            <Wallet className="h-4 w-4 mr-1" /> {paidTotal > 0 ? "Restzahlung erfassen" : "Direktzahlung"}
          </Button>
        )}
        {activeInvoice && unbilledOpenTotal > 0.005 && (
          <Button
            className="border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100"
            variant="outline"
            onClick={() => setSettleOpen(true)}
            data-testid="settle-unbilled-balance"
          >
            <Wallet className="h-4 w-4 mr-1" /> Restbetrag abrechnen ({fmtEur(unbilledOpenTotal)})
          </Button>
        )}
        {!isLocked ? (
          <Button
            variant="outline"
            className="border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
            onClick={() => { if (confirm("Arbeit abschließen? Timer wird gestoppt und der Auftrag als 'Abgeschlossen' markiert.")) completeMut.mutate(); }}
            disabled={completeMut.isPending}
            data-testid="complete-job"
          >
            <Square className="h-4 w-4 mr-1" /> Arbeit abschließen
          </Button>
        ) : (
          <Button
            variant="outline"
            onClick={() => { if (confirm("Auftrag wieder öffnen? Der Timer wird freigeschaltet und der Status auf 'In Bearbeitung' gesetzt.")) reopenMut.mutate(); }}
            disabled={reopenMut.isPending}
            data-testid="reopen-job"
          >
            <RotateCcw className="h-4 w-4 mr-1" /> Auftrag wieder öffnen
          </Button>
        )}
        {job.status !== "archiviert" && (
          <Button className="border-slate-200 bg-slate-50 text-slate-700 hover:bg-slate-100" variant="outline" onClick={() => { if (confirm("Auftrag archivieren?")) archiveMut.mutate(); }} data-testid="archive-job">
            <Archive className="h-4 w-4 mr-1" /> Archivieren
          </Button>
        )}
        {!isLocked && (
          <>
            <Link to={`/werkstatt?job=${job.id}&returnTo=${encodeURIComponent(`/auftraege/${job.id}`)}`} data-testid="open-workshop-mode"
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-xl text-sm border border-slate-200 bg-white hover:bg-slate-50">
              <Maximize2 className="h-4 w-4" /> Im Werkstatt-Modus öffnen
            </Link>
            <Button variant="ghost" className="text-destructive ml-auto" onClick={() => { if (confirm("Auftrag UNWIDERRUFLICH löschen?")) hardDeleteMut.mutate(); }} data-testid="delete-job">
              <Trash2 className="h-4 w-4 mr-1" /> Löschen
            </Button>
          </>
        )}
      </div>
      </section>

      <Dialog open={settleOpen} onOpenChange={setSettleOpen}>
        <DialogContent className="max-w-md" data-testid="settle-balance-dialog">
          <DialogHeader>
            <DialogTitle>Restbetrag abrechnen</DialogTitle>
          </DialogHeader>
          <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
            <div className="text-xs font-semibold uppercase tracking-wide text-amber-700">Noch nicht berechnet</div>
            <div className="mt-1 font-display text-2xl font-bold text-amber-900">{fmtEur(unbilledOpenTotal)}</div>
            <div className="mt-1 text-xs text-amber-800">Dieser Betrag wurde nach der bisherigen Rechnung zum Auftrag hinzugefügt.</div>
          </div>
          {invoiceOpenTotal > 0.005 && (
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800">
              Zusätzlich sind {fmtEur(invoiceOpenTotal)} aus der bestehenden Rechnung offen. Dieser Teil wird direkt in der Rechnung bezahlt.
            </div>
          )}
          <div className="grid gap-2">
            <Button
              type="button"
              className="h-auto justify-start bg-amber-500 px-4 py-3 text-left text-white hover:bg-amber-600"
              onClick={() => { setSettleOpen(false); setDirectPayOpen(true); }}
              data-testid="settle-direct-payment"
            >
              <Wallet className="mr-3 h-5 w-5 shrink-0" />
              <span><strong className="block">Ohne Rechnung bezahlen</strong><span className="text-xs font-normal opacity-90">Restzahlung direkt erfassen</span></span>
            </Button>
            <Button
              type="button"
              variant="outline"
              className="h-auto justify-start border-blue-200 bg-blue-50 px-4 py-3 text-left text-blue-800 hover:bg-blue-100"
              onClick={() => supplementInvoiceMut.mutate()}
              disabled={supplementInvoiceMut.isPending}
              data-testid="create-supplement-invoice"
            >
              <FileText className="mr-3 h-5 w-5 shrink-0" />
              <span><strong className="block">Zusatzrechnung erstellen</strong><span className="text-xs font-normal">Neuer Entwurf nur über {fmtEur(unbilledOpenTotal)}</span></span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Sprint 23 — Direktzahlung dialog (reused from Payments page) */}
      <DirectPaymentDialog
        open={directPayOpen}
        onOpenChange={setDirectPayOpen}
        defaultCustomerId={job.customer_id || ""}
        defaultCustomerName={job.customer_name || ""}
        defaultAmount={activeInvoice ? unbilledOpenTotal : openTotal}
        defaultJobId={job.id}
        defaultVehicleId={job.vehicle_id || null}
        onSuccess={() => {
          qc.invalidateQueries({ queryKey: ["job", id] });
          qc.invalidateQueries({ queryKey: ["jobs"] });
          qc.invalidateQueries({ queryKey: ["payments"] });
          qc.invalidateQueries({ queryKey: ["customer", job.customer_id] });
        }}
      />

      {/* Photo dialog */}
      {photoDialogOpen && (
        <Dialog open onOpenChange={(o) => { if (!o) setPhotoDialogOpen(false); }}>
          <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display">Fotos & Dokumente</DialogTitle></DialogHeader>
            <PhotoUploader
              jobId={id}
              photos={job.photos}
              readOnly={isLocked}
              onChanged={() => qc.invalidateQueries({ queryKey: ["job", id] })}
              onPreview={(photoId) => {
                setPhotoDialogOpen(false);
                setLightboxId(photoId);
              }}
            />
          </DialogContent>
        </Dialog>
      )}
      <PhotoLightbox
        photos={(job.photos || []).filter((photo) => photo.content_type?.startsWith("image/"))}
        activeId={lightboxId}
        onClose={() => setLightboxId(null)}
      />
    </div>
  );
}

// ── Compact timer strip (Sprint 16) ─────────────────────────────────
function JobTimerCompact({ job }) {
  const [tick, setTick] = useState(0);
  const state = job.timer_state || "idle";
  useEffect(() => {
    if (state !== "running") return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [state]);
  const liveExtra = state === "running" && job.timer_last_resumed_at
    ? Math.max(0, (Date.now() - new Date(job.timer_last_resumed_at).getTime()) / 1000)
    : 0;
  const totalSec = (job.total_work_seconds || 0) + liveExtra;
  // eslint-disable-next-line no-unused-vars
  const _t = tick;
  const meta = TIMER_STATE_META[state] || TIMER_STATE_META.idle;
  // Sprint 24b — show the "Arbeitszeit gesamt" summary on the compact card when done.
  const isCompleted = job.status === "fertig";
  const content = (
    <>
      <div className="flex items-center gap-2">
        <div className="stellar-section-icon stellar-section-orange"><Timer /></div>
        <div className="font-display font-semibold flex-1">
          {isCompleted ? "Arbeitszeit gesamt" : "Werkstatt-Timer"}
        </div>
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${meta.cls}`} data-testid="timer-state">{meta.label}</span>
      </div>
      <div className="stellar-timer-content">
        <div className="font-mono text-3xl font-bold tabular-nums text-amber-500" data-testid="timer-display">
          {isCompleted ? fmtHumanTotal(job.total_work_seconds || 0) : fmtDuration(totalSec)}
        </div>
        <div className="stellar-timer-link">
          {isCompleted
            ? <><CheckCircle2 className="h-3.5 w-3.5" /> Arbeitszeit abgeschlossen</>
            : <><Maximize2 className="h-3.5 w-3.5" /> Im Werkstatt-Modus öffnen →</>}
        </div>
      </div>
    </>
  );
  if (isCompleted) {
    return <div data-testid="job-timer-compact" className="premium-card stellar-timer-card p-5">{content}</div>;
  }
  return (
    <Link to={`/werkstatt?job=${job.id}&returnTo=${encodeURIComponent(`/auftraege/${job.id}`)}`} data-testid="job-timer-compact"
      className="premium-card stellar-timer-card p-5 hover:bg-slate-50 transition">
      {content}
    </Link>
  );
}

// Sprint 24b — human total format, e.g. "14 h 37 min" (matches WorkshopMode helper).
function fmtHumanTotal(totalSec) {
  const s = Math.max(0, Math.round(totalSec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h === 0 && m === 0) return "0 min";
  if (h === 0) return `${m} min`;
  if (m === 0) return `${h} h`;
  return `${h} h ${m} min`;
}

// ─── Job Timer (Sprint 9) ─────────────────────────────────────────────
function fmtDuration(totalSec) {
  const s = Math.max(0, Math.round(totalSec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

const TIMER_STATE_META = {
  idle:     { label: "Nicht gestartet", cls: "bg-muted text-muted-foreground" },
  running:  { label: "Läuft",           cls: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400" },
  paused:   { label: "Pausiert",        cls: "bg-amber-500/10 text-amber-700 dark:text-amber-400" },
  finished: { label: "Abgeschlossen",   cls: "bg-blue-500/10 text-blue-700 dark:text-blue-400" },
};

function JobTimer({ job, onChanged }) {
  const [tick, setTick] = useState(0);
  const state = job.timer_state || "idle";

  useEffect(() => {
    if (state !== "running" && state !== "paused") return undefined;
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, [state]);

  // Live elapsed: total_work_seconds + (now - timer_last_resumed_at) when running
  const liveExtra = state === "running" && job.timer_last_resumed_at
    ? Math.max(0, (Date.now() - new Date(job.timer_last_resumed_at).getTime()) / 1000)
    : 0;
  const totalSec = (job.total_work_seconds || 0) + liveExtra;
  const pausedSec = state === "paused" && job.paused_since
    ? Math.max(0, (Date.now() - new Date(job.paused_since).getTime()) / 1000)
    : 0;
  // eslint-disable-next-line no-unused-vars
  const _keepTicking = tick;

  const call = async (endpoint) => (await api.post(`/jobs/${job.id}/timer/${endpoint}`)).data;

  const startMut = useMutation({ mutationFn: () => call("start"),
    onSuccess: () => { toast.success("Timer gestartet"); onChanged(); }, onError: (e) => toast.error(apiError(e)) });
  const pauseMut = useMutation({ mutationFn: () => call("pause"),
    onSuccess: () => { toast.success("Timer pausiert"); onChanged(); }, onError: (e) => toast.error(apiError(e)) });
  const resumeMut = useMutation({ mutationFn: () => call("resume"),
    onSuccess: () => { toast.success("Timer fortgesetzt"); onChanged(); }, onError: (e) => toast.error(apiError(e)) });
  const finishMut = useMutation({ mutationFn: () => call("finish"),
    onSuccess: () => { toast.success("Timer abgeschlossen"); onChanged(); }, onError: (e) => toast.error(apiError(e)) });
  const resetMut = useMutation({ mutationFn: () => call("reset"),
    onSuccess: () => { toast.success("Timer zurückgesetzt"); onChanged(); }, onError: (e) => toast.error(apiError(e)) });

  const meta = TIMER_STATE_META[state] || TIMER_STATE_META.idle;
  const estimatedMin = job.estimated_duration_min || 0;

  return (
    <div className="rounded-lg border border-border/60 bg-card p-5" data-testid="job-timer">
      <div className="flex items-center gap-2 mb-3">
        <Timer className="h-5 w-5 text-primary" />
        <h2 className="font-display font-semibold">Werkstatt-Timer</h2>
        <span className={`ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium ${meta.cls}`} data-testid="timer-state">
          {meta.label}
        </span>
      </div>

      <div className="text-center py-4 space-y-3">
        {estimatedMin > 0 && (
          <div data-testid="job-timer-estimated">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Geschätzt</div>
            <div className="font-mono text-lg font-semibold text-muted-foreground">
              {String(Math.floor(estimatedMin / 60)).padStart(2, "0")}:{String(estimatedMin % 60).padStart(2, "0")} h
            </div>
          </div>
        )}
        <div>
          {job.status === "fertig" ? (
            <>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Arbeitszeit gesamt</div>
              <div className="font-mono text-4xl font-bold tabular-nums tracking-tight" data-testid="timer-total-worktime">
                {fmtHumanTotal(job.total_work_seconds || 0)}
              </div>
            </>
          ) : (
            <>
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Läuft</div>
              <div className="font-mono text-4xl font-bold tabular-nums tracking-tight" data-testid="timer-display" suppressHydrationWarning>
                {fmtDuration(totalSec)}
              </div>
            </>
          )}
        </div>
        {state === "paused" && job.paused_since && (
          <div className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full bg-amber-500/10 border border-amber-500/40 text-amber-700 dark:text-amber-400 text-xs" data-testid="job-timer-paused-since">
            <AlertTriangle className="h-3 w-3" />
            Pausiert seit {(() => {
              const s = Math.round(pausedSec);
              const h = Math.floor(s / 3600); const m = Math.floor((s % 3600) / 60);
              return h > 0 ? `${h}h ${m}m` : `${m}m`;
            })()}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-2 justify-center">
        {state === "idle" && (
          <Button onClick={() => startMut.mutate()} disabled={startMut.isPending} data-testid="timer-start">
            <Play className="h-4 w-4 mr-1" /> Starten
          </Button>
        )}
        {state === "running" && (
          <>
            <Button variant="outline" onClick={() => pauseMut.mutate()} disabled={pauseMut.isPending} data-testid="timer-pause">
              <Pause className="h-4 w-4 mr-1" /> Pause
            </Button>
            <Button variant="outline" onClick={() => { if (confirm("Timer abschließen? Der Timer kann nicht erneut gestartet werden.")) finishMut.mutate(); }} disabled={finishMut.isPending} data-testid="timer-finish">
              <Square className="h-4 w-4 mr-1" /> Abschließen
            </Button>
          </>
        )}
        {state === "paused" && (
          <>
            <Button onClick={() => resumeMut.mutate()} disabled={resumeMut.isPending} data-testid="timer-resume">
              <Play className="h-4 w-4 mr-1" /> Fortsetzen
            </Button>
            <Button variant="outline" onClick={() => { if (confirm("Timer abschließen? Der Timer kann nicht erneut gestartet werden.")) finishMut.mutate(); }} disabled={finishMut.isPending} data-testid="timer-finish">
              <Square className="h-4 w-4 mr-1" /> Abschließen
            </Button>
          </>
        )}
        {state === "finished" && (
          <div className="text-xs text-muted-foreground italic">
            Abgeschlossen am {job.timer_finished_at ? new Date(job.timer_finished_at).toLocaleString("de-DE") : "—"}
          </div>
        )}
        {state !== "idle" && state !== "finished" && (
          <Link to={`/werkstatt?job=${job.id}&returnTo=${encodeURIComponent(`/auftraege/${job.id}`)}`} data-testid="timer-open-workshop"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs border border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 hover:bg-emerald-500/20">
            <Maximize2 className="h-3.5 w-3.5" /> Werkstatt-Modus
          </Link>
        )}
        {state === "idle" && (
          <Link to={`/werkstatt?job=${job.id}&returnTo=${encodeURIComponent(`/auftraege/${job.id}`)}`} data-testid="timer-open-workshop"
            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md text-xs border border-border/60 hover:bg-muted">
            <Maximize2 className="h-3.5 w-3.5" /> Werkstatt-Modus öffnen
          </Link>
        )}
        {state !== "idle" && (
          <Button variant="ghost" size="sm" className="text-destructive" onClick={() => { if (confirm("Timer komplett zurücksetzen? Gemessene Zeit geht verloren.")) resetMut.mutate(); }} data-testid="timer-reset">
            <RotateCcw className="h-4 w-4 mr-1" /> Zurücksetzen
          </Button>
        )}
      </div>

      {job.timer_started_at && (
        <div className="mt-4 pt-3 border-t border-border/60 grid grid-cols-2 gap-3 text-xs">
          <div>
            <div className="text-muted-foreground">Gestartet</div>
            <div className="font-medium">{new Date(job.timer_started_at).toLocaleString("de-DE")}</div>
          </div>
          {job.timer_finished_at && (
            <div>
              <div className="text-muted-foreground">Abgeschlossen</div>
              <div className="font-medium">{new Date(job.timer_finished_at).toLocaleString("de-DE")}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
